/**
 * TransactionDetailModal Component
 * Displays detailed information for a transaction based on its type.
 * Supports payment, wallet credit/debit, and transfer transactions.
 */

import React from "react";
import {
  Modal,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import Button from "../Button";
import * as Colors from "../../styles/colors";
import * as Fonts from "../../styles/typography";
import {
  TRANSACTION_TYPES,
  TRANSACTION_TYPE_LABELS,
  TRANSACTION_STATUS_LABELS,
  formatTransactionAmount,
  formatTransactionDate,
  getTransactionTypeIcon,
  getTransactionAmountColor,
  getTransactionStatusColor,
} from "../../helpers/transactionHelpers";

/**
 * TransactionDetailModal - Displays full transaction details in a modal.
 * @param {boolean} visible - Whether the modal is visible
 * @param {Object} transaction - The transaction detail object
 * @param {Function} onClose - Callback when modal is closed
 */
export default function TransactionDetailModal({ visible, transaction, onClose }) {
  if (!transaction) return null;

  const renderDetailRow = (label, value, valueStyle = {}) => {
    if (value === null || value === undefined || value === "") return null;
    return (
      <View style={styles.detailInfoRow}>
        <Text style={styles.detailInfoLabel}>{label}:</Text>
        <Text style={[styles.detailInfoValue, valueStyle]}>{value}</Text>
      </View>
    );
  };

  const renderContent = () => {
    switch (transaction.transactionType) {
      case TRANSACTION_TYPES.PAYMENT:
        return renderPaymentDetail();
      case TRANSACTION_TYPES.WALLET_CREDIT:
      case TRANSACTION_TYPES.WALLET_DEBIT:
        return renderWalletOperationDetail();
      case TRANSACTION_TYPES.TRANSFER:
        return renderTransferDetail();
      default:
        return renderGenericDetail();
    }
  };


  // Payment transaction detail view (Requirement 5.1)
  const renderPaymentDetail = () => (
    <View style={styles.detailContent}>
      {/* Transaction Header */}
      <View style={styles.detailSection}>
        <View style={styles.detailRow}>
          <Icon
            name={getTransactionTypeIcon(transaction.transactionType)}
            size={32}
            color={Colors.PRIMARY}
          />
          <View style={styles.detailRowContent}>
            <Text style={styles.detailLabel}>Tipo de Transação</Text>
            <Text style={styles.detailValue}>
              {TRANSACTION_TYPE_LABELS[transaction.transactionType] || "Pagamento"}
            </Text>
          </View>
        </View>
      </View>

      {/* Transaction Info */}
      <View style={styles.detailSection}>
        <Text style={styles.detailSectionTitle}>Informações da Transação</Text>
        {renderDetailRow("ID da Transação", transaction.transactionId)}
        {renderDetailRow("Data/Hora", formatTransactionDate(transaction.dateTime))}
        {renderDetailRow("Número do Ticket", transaction.ticketNumber)}
        {renderDetailRow("Placa", transaction.plate)}
        {renderDetailRow("Local", transaction.unityName)}
      </View>

      {/* Payment Info */}
      <View style={styles.detailSection}>
        <Text style={styles.detailSectionTitle}>Pagamento</Text>
        {renderDetailRow(
          "Valor",
          formatTransactionAmount(transaction.amount),
          { color: getTransactionAmountColor(transaction.transactionType, transaction.amount) }
        )}
        {renderDetailRow(
          "Status",
          TRANSACTION_STATUS_LABELS[transaction.status] || transaction.status,
          { color: getTransactionStatusColor(transaction.status) }
        )}
        {renderDetailRow("Método de Pagamento", getPaymentMethodLabel(transaction.paymentMethod))}
        {transaction.lastCardDigits && renderDetailRow("Cartão", `**** ${transaction.lastCardDigits}`)}
      </View>

      {/* User Info */}
      <View style={styles.detailSection}>
        <Text style={styles.detailSectionTitle}>Usuário</Text>
        {renderDetailRow("Nome", transaction.userName)}
        {renderDetailRow("Email", transaction.userEmail)}
      </View>
    </View>
  );


  // Wallet operation detail view (Requirement 5.2)
  const renderWalletOperationDetail = () => {
    const isCredit = transaction.transactionType === TRANSACTION_TYPES.WALLET_CREDIT;
    const isAdminAdjustment = transaction.adminUserId || transaction.adminEmail;

    return (
      <View style={styles.detailContent}>
        {/* Transaction Header */}
        <View style={styles.detailSection}>
          <View style={styles.detailRow}>
            <Icon
              name={getTransactionTypeIcon(transaction.transactionType)}
              size={32}
              color={isCredit ? Colors.SUCCESS : Colors.ALERT}
            />
            <View style={styles.detailRowContent}>
              <Text style={styles.detailLabel}>Tipo de Transação</Text>
              <Text style={styles.detailValue}>
                {TRANSACTION_TYPE_LABELS[transaction.transactionType]}
              </Text>
            </View>
          </View>
        </View>

        {/* Transaction Info */}
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Informações da Transação</Text>
          {renderDetailRow("ID da Transação", transaction.transactionId)}
          {renderDetailRow("Data/Hora", formatTransactionDate(transaction.dateTime))}
        </View>

        {/* Balance Info */}
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Saldo</Text>
          {renderDetailRow("Saldo Anterior", formatTransactionAmount(transaction.previousBalance))}
          {renderDetailRow("Saldo Atual", formatTransactionAmount(transaction.newBalance))}
          {renderDetailRow(
            "Valor",
            formatTransactionAmount(transaction.amount),
            { color: getTransactionAmountColor(transaction.transactionType, transaction.amount) }
          )}
        </View>

        {/* Admin Adjustment Info (if applicable) */}
        {isAdminAdjustment && (
          <View style={styles.detailSection}>
            <Text style={styles.detailSectionTitle}>Ajuste Administrativo</Text>
            {renderDetailRow("Motivo", transaction.reason)}
            {renderDetailRow("Admin ID", transaction.adminUserId)}
            {renderDetailRow("Admin Email", transaction.adminEmail)}
          </View>
        )}

        {/* Admin Adjustment Info (if applicable) */}
        {!isAdminAdjustment && (
          <View style={styles.detailSection}>
            <Text style={styles.detailSectionTitle}>Pagamento</Text>
            {renderDetailRow(
              "Status",
              TRANSACTION_STATUS_LABELS[transaction.status] || transaction.status,
              { color: getTransactionStatusColor(transaction.status) }
            )}
            {renderDetailRow("Método de Pagamento", getPaymentMethodLabel(transaction.paymentMethod))}
            {transaction.lastCardDigits && renderDetailRow("Cartão", `**** ${transaction.lastCardDigits}`)}
          </View>
        )}

        {/* User Info */}
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Usuário</Text>
          {renderDetailRow("Nome", transaction.userName)}
          {renderDetailRow("Email", transaction.userEmail)}
        </View>
      </View>
    );
  };


  // Transfer detail view (Requirement 5.3)
  const renderTransferDetail = () => {
    const isIncoming = transaction.amount >= 0;

    return (
      <View style={styles.detailContent}>
        {/* Transaction Header */}
        <View style={styles.detailSection}>
          <View style={styles.detailRow}>
            <Icon
              name={getTransactionTypeIcon(transaction.transactionType)}
              size={32}
              color={isIncoming ? Colors.SUCCESS : Colors.ALERT}
            />
            <View style={styles.detailRowContent}>
              <Text style={styles.detailLabel}>Tipo de Transação</Text>
              <Text style={styles.detailValue}>
                {TRANSACTION_TYPE_LABELS[transaction.transactionType]}
              </Text>
            </View>
          </View>
        </View>

        {/* Transaction Info */}
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Informações da Transação</Text>
          {renderDetailRow("ID da Transação", transaction.transactionId)}
          {renderDetailRow("Data/Hora", formatTransactionDate(transaction.dateTime))}
          {renderDetailRow(
            "Valor",
            formatTransactionAmount(Math.abs(transaction.amount)),
            { color: getTransactionAmountColor(transaction.transactionType, transaction.amount) }
          )}
        </View>

        {/* Sender Info */}
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Remetente</Text>
          {renderDetailRow("Nome", transaction.senderName)}
          {renderDetailRow("Email", transaction.senderEmail)}
        </View>

        {/* Receiver Info */}
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Destinatário</Text>
          {renderDetailRow("Nome", transaction.receiverName)}
          {renderDetailRow("Email", transaction.receiverEmail)}
        </View>
      </View>
    );
  };


  // Generic detail view for unknown transaction types
  const renderGenericDetail = () => (
    <View style={styles.detailContent}>
      <View style={styles.detailSection}>
        <View style={styles.detailRow}>
          <Icon
            name="help-circle-outline"
            size={32}
            color={Colors.GRAY_DARK}
          />
          <View style={styles.detailRowContent}>
            <Text style={styles.detailLabel}>Tipo de Transação</Text>
            <Text style={styles.detailValue}>
              {TRANSACTION_TYPE_LABELS[transaction.transactionType] || transaction.transactionType || "Desconhecido"}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.detailSection}>
        <Text style={styles.detailSectionTitle}>Informações da Transação</Text>
        {renderDetailRow("ID da Transação", transaction.transactionId)}
        {renderDetailRow("Data/Hora", formatTransactionDate(transaction.dateTime))}
        {renderDetailRow(
          "Valor",
          formatTransactionAmount(transaction.amount),
          { color: getTransactionAmountColor(transaction.transactionType, transaction.amount) }
        )}
        {renderDetailRow(
          "Status",
          TRANSACTION_STATUS_LABELS[transaction.status] || transaction.status,
          { color: getTransactionStatusColor(transaction.status) }
        )}
      </View>

      <View style={styles.detailSection}>
        <Text style={styles.detailSectionTitle}>Usuário</Text>
        {renderDetailRow("Nome", transaction.userName)}
        {renderDetailRow("Email", transaction.userEmail)}
      </View>
    </View>
  );

  // Helper function to get payment method label in Portuguese
  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case "credit_card":
        return "Cartão de Crédito";
      case "wallet":
        return "Carteira";
      case "pix":
        return "PIX";
      default:
        return method || "N/A";
    }
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.detailModal}>
          <View style={styles.detailHeader}>
            <Text style={styles.detailTitle}>Detalhes da Transação</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Icon name="close" size={24} color={Colors.BLACK} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.scrollContent}>
            {renderContent()}
          </ScrollView>

          <Button
            textoBotao="Fechar"
            estilo={styles.closeModalButton}
            onPress={onClose}
          />
        </View>
      </View>
    </Modal>
  );
}


const styles = StyleSheet.create({
  // Modal overlay and container
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  detailModal: {
    backgroundColor: Colors.WHITE,
    borderRadius: 12,
    width: "90%",
    maxHeight: "85%",
  },
  detailHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  detailTitle: {
    fontSize: Fonts.FONT_SIZE_18,
    fontWeight: "bold",
    color: Colors.BLACK,
  },
  closeButton: {
    padding: 4,
  },
  scrollContent: {
    maxHeight: "70%",
  },
  detailContent: {
    padding: 16,
  },
  detailSection: {
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  detailRowContent: {
    marginLeft: 12,
  },
  detailLabel: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.GRAY_DARK,
  },
  detailValue: {
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.BLACK,
    fontWeight: "bold",
  },
  detailSectionTitle: {
    fontSize: Fonts.FONT_SIZE_14,
    fontWeight: "bold",
    color: Colors.BLACK,
    marginBottom: 8,
  },
  detailInfoRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  detailInfoLabel: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    width: 140,
  },
  detailInfoValue: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
    flex: 1,
  },
  closeModalButton: {
    backgroundColor: Colors.PRIMARY,
    margin: 16,
    marginTop: 0,
  },
});
